/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/BusinessEventRooms/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger } from "../../utils/logger.js";
import { compactSelectStyles } from "../../utils/reactSelectControlStyles.js";
import styles from "./index.module.css";
import { useState, useEffect } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import { getNumberOfTablesOptions } from "../../data/selectOptions";
import { getSelectedOption } from "../../utils/common";
import { ReactComponent as AddIcon } from "../../assets/icons/BusinessEvents/AddIcon.svg";

const BusinessEventRooms = ({ roomsData, maxSpaces, onRoomsChange }) => {
  const { t } = useTranslation();
  const [rooms, setRooms] = useState([]);

  useEffect(() => {
    if (roomsData) {
      setRooms(roomsData);
    }
  }, [roomsData]);

  const updateRooms = (newRooms) => {
    setRooms(newRooms);
    if (onRoomsChange) {
      onRoomsChange(newRooms);
    }
  };

  const handleRoomNameChange = (e, index) => {
    const newRooms = [...rooms];
    newRooms[index].nombre = e.target.value;
    updateRooms(newRooms);
  };

  const handleTableNameChange = (e, roomIndex, tableIndex) => {
    const newRooms = [...rooms];
    newRooms[roomIndex].mesas[tableIndex].nombre = e.target.value;
    updateRooms(newRooms);
  };

  const handleNumberOfTablesChange = (selectedOption, roomIndex) => {
    logger.info("handleNumberOfTablesChange: selectedOption: ", {
      data: selectedOption,
    });
    const newRooms = [...rooms];
    const currentMesas = [...newRooms[roomIndex].mesas];
    const newValue = selectedOption.value;

    newRooms[roomIndex].cantidadMesas = newValue;

    if (newValue < currentMesas.length) {
      // Truncate if new value is less than current length
      newRooms[roomIndex].mesas = currentMesas.slice(0, newValue);
    } else if (newValue > currentMesas.length) {
      // Keep existing mesas and add new empty ones if new value is greater
      newRooms[roomIndex].mesas = [...currentMesas];
      for (let i = currentMesas.length; i < newValue; i++) {
        newRooms[roomIndex].mesas.push({
          nombre: "",
          numeroEspacios: 1,
        });
      }
    } else {
      // newValue === currentMesas.length -> intentionally no change
    }

    updateRooms(newRooms);
  };

  const handleNumberOfSpacesChange = (
    selectedOption,
    roomIndex,
    tableIndex,
  ) => {
    const newRooms = [...rooms];
    newRooms[roomIndex].mesas[tableIndex].numeroEspacios = selectedOption.value;
    updateRooms(newRooms);
  };

  const handleDeleteRoom = (roomIndex) => {
    const newRooms = [...rooms];
    newRooms.splice(roomIndex, 1);
    updateRooms(newRooms);
  };

  const handleAddRoom = () => {
    const newRooms = [...rooms];
    newRooms.push({
      nombre: "",
      cantidadMesas: 1,
      mesas: [{ nombre: "", numeroEspacios: 1 }],
    });
    updateRooms(newRooms);
  };

  const formatDateString = (dateString) => {
    // Format "YYYY-MM-DD" to "DD/MM/YYYY"
    if (!dateString) {
      return "";
    }
    const parts = dateString.split("-");
    if (parts.length === 3) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return dateString;
  };

  return (
    <div className={styles.businessEventRoomsContainer}>
      <div className={styles.sectionTitle}>
        <span>{t("components.businessEventRooms.roomsRegistration")}</span>
      </div>
      {maxSpaces &&
        typeof maxSpaces === "object" &&
        Object.keys(maxSpaces).length > 0 && (
          <div className={styles.maxSpacesContainer}>
            <div className={styles.maxSpacesTitle}>
              <span>{t("components.businessEventRooms.maxSpacesByDate")}</span>
            </div>
            <table className={styles.maxSpacesTable}>
              <thead>
                <tr>
                  <th>{t("components.businessEventRooms.date")}</th>
                  <th>{t("components.businessEventRooms.numberOfSpaces")}</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(maxSpaces)
                  .sort(([dateA], [dateB]) => {
                    // Sort dates chronologically
                    const date1 = new Date(dateA);
                    const date2 = new Date(dateB);
                    return date1 - date2;
                  })
                  .map(([date, spaces]) => (
                    <tr key={date}>
                      <td>{formatDateString(date)}</td>
                      <td>{spaces}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      {rooms &&
        rooms.length > 0 &&
        rooms.map((room, roomIndex) => (
          <div key={roomIndex} className={styles.businessEventRoom}>
            <span>
              {t("components.businessEventRooms.room") + " " + (roomIndex + 1)}
            </span>
            <div className={styles.businessEventRoomItem}>
              <div className={styles.roomItemContent}>
                <div className={styles.nameCol}>
                  <span>{t("components.businessEventRooms.roomName")}</span>
                  <input
                    type="text"
                    name="nombre"
                    value={room.nombre}
                    onChange={(e) => handleRoomNameChange(e, roomIndex)}
                    className={styles.input}
                  />
                </div>
                <div className={styles.numberOfTables}>
                  <span>
                    {t("components.businessEventRooms.numberOfTables")}
                  </span>
                  <Select
                    options={getNumberOfTablesOptions()}
                    value={getSelectedOption(
                      getNumberOfTablesOptions(),
                      room.cantidadMesas,
                    )}
                    onChange={(selectedOption) =>
                      handleNumberOfTablesChange(selectedOption, roomIndex)
                    }
                    className={styles.numberOfTablesSelect}
                    styles={compactSelectStyles}
                  />
                </div>
                <div className={styles.deleteRoomButton}>
                  <button
                    className={styles.inputClearButton}
                    onClick={() => handleDeleteRoom(roomIndex)}
                  >
                    &times;
                  </button>
                </div>
              </div>
              <div className={styles.roomTables}>
                {room.mesas.map((table, tableIndex) => (
                  <div key={tableIndex} className={styles.roomTable}>
                    <span className={styles.tableName}>
                      {t("components.businessEventRooms.table") +
                        " " +
                        (tableIndex + 1)}
                    </span>
                    <div className={styles.tableItemContent}>
                      <div className={styles.nameCol}>
                        <span>
                          {t("components.businessEventRooms.tableName")}
                        </span>
                        <input
                          type="text"
                          name="nombre"
                          value={table.nombre}
                          onChange={(e) =>
                            handleTableNameChange(e, roomIndex, tableIndex)
                          }
                          className={styles.input}
                        />
                      </div>
                      <div className={styles.numberOfSpaces}>
                        <span>{t("components.businessEventRooms.spaces")}</span>
                        <Select
                          options={getNumberOfTablesOptions()}
                          value={getSelectedOption(
                            getNumberOfTablesOptions(),
                            table.numeroEspacios,
                          )}
                          onChange={(selectedOption) =>
                            handleNumberOfSpacesChange(
                              selectedOption,
                              roomIndex,
                              tableIndex,
                            )
                          }
                          className={styles.numberOfTablesSelect}
                          styles={{
                            control: (baseStyles) => ({
                              ...baseStyles,
                              borderRadius: "8px",
                              width: "80px",
                              height: "40px",
                              fontSize: "16px",
                              cursor: "pointer",
                              textAlign: "left",
                              borderColor: "#909092",
                            }),
                            container: (baseStyles) => ({
                              ...baseStyles,
                              width: "80px",
                            }),
                            valueContainer: (baseStyles) => ({
                              ...baseStyles,
                            }),
                            input: (baseStyles) => ({
                              ...baseStyles,
                            }),
                            option: (baseStyles) => ({
                              ...baseStyles,
                              cursor: "pointer",
                            }),
                            menu: (baseStyles) => ({
                              ...baseStyles,
                              maxHeight: "75px",
                              overflowY: "auto",
                            }),
                            menuList: (baseStyles) => ({
                              ...baseStyles,
                              maxHeight: "75px",
                              overflowY: "auto",
                              padding: "4px 0",
                            }),
                            indicatorSeparator: () => ({ display: "none" }),
                          }}
                        />
                      </div>
                    </div>
                    {/* <div className={styles.deleteTableButton}>
                    <button onClick={() => handleDeleteTable(index, tableIndex)}>
                      {t("components.businessEventRooms.delete")}
                    </button>
                  </div> */}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      <Button
        variant="contained"
        className={styles.addRoomButton}
        onClick={handleAddRoom}
        startIcon={<AddIcon />}
      >
        {t("components.businessEventRooms.addRoom")}
      </Button>
    </div>
  );
};

export default BusinessEventRooms;
